 
"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useRecordsMutations } from "@/hooks/use-records";
import { createUploadStore } from "@/frameworks/upload/createUploadStore";
import type {  } from "@/presentation/upload/stores/UploadStore";
import type { UploadTaskViewModel } from "@/presentation/upload/view-models/UploadTaskViewModel";
import { mapUploadTaskDtoToViewModel } from "@/presentation/upload/view-models/UploadTaskViewModel";
import { UPLOAD_SOURCES, type UploadSource } from "@/lib/analytics/types";

type TextFileModalViewModel = {
  isVisible: boolean;
  data: {
    taskId: string;
    name: string;
    content: string;
    extension: "txt" | "md" | "srt" | "vtt";
    uploadSource?: UploadSource;
  } | null;
  projectId?: string;
};

const normalizeDuration = (value: number | null | undefined): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const isSamePlaceholderRecord = (
  prev: UploadTaskViewModel["placeholderRecord"],
  next: UploadTaskViewModel["placeholderRecord"]
): boolean => {
  if (!prev && !next) {
    return true;
  }
  if (!prev || !next) {
    return false;
  }
  return (
    prev.id === next.id &&
    prev.name === next.name &&
    prev.project === next.project &&
    prev.status === next.status &&
    prev.fileName === next.fileName &&
    prev.fileExtension === next.fileExtension &&
    prev.sizeMb === next.sizeMb &&
    normalizeDuration(prev.durationSec) === normalizeDuration(next.durationSec) &&
    prev.createdAt === next.createdAt &&
    prev.updatedAt === next.updatedAt &&
    prev.type === next.type
  );
};

const isSameTaskViewModel = (
  prev: UploadTaskViewModel,
  next: UploadTaskViewModel
): boolean => {
  return (
    prev.fileId === next.fileId &&
    prev.name === next.name &&
    prev.originalFileName === next.originalFileName &&
    prev.status === next.status &&
    prev.progress === next.progress &&
    prev.type === next.type &&
    prev.sizeMb === next.sizeMb &&
    normalizeDuration(prev.durationSec) === normalizeDuration(next.durationSec) &&
    prev.error === next.error &&
    isSamePlaceholderRecord(prev.placeholderRecord, next.placeholderRecord)
  );
};

const isSameTextFileModalData = (
  prev: TextFileModalViewModel["data"],
  next: TextFileModalViewModel["data"]
): boolean => {
  if (!prev && !next) {
    return true;
  }
  if (!prev || !next) {
    return false;
  }
  return (
    prev.taskId === next.taskId &&
    prev.name === next.name &&
    prev.content === next.content &&
    prev.extension === next.extension &&
    prev.uploadSource === next.uploadSource
  );
};

const isSameTextFileModal = (
  prev: TextFileModalViewModel,
  next: TextFileModalViewModel
): boolean => {
  return (
    prev.isVisible === next.isVisible &&
    prev.projectId === next.projectId &&
    isSameTextFileModalData(prev.data, next.data)
  );
};

const isUploadSource = (value: string | undefined): value is UploadSource => {
  if (!value) {
    return false;
  }
  return (UPLOAD_SOURCES as readonly string[]).includes(value);
};

export interface UploadFilesContextType {
  uploadNewFile: (
    file: File,
    projectId: string | undefined,
    uploadSource: UploadSource
  ) => Promise<void>;
  uploadMultipleFiles: (
    files: File[],
    projectId: string | undefined,
    uploadSource: UploadSource
  ) => Promise<void>;
  uploadFileTasks: Map<string, UploadTaskViewModel>;
  removeTask: (taskId: string) => void;
  cancelUpload: (taskId: string) => void;
  isUploading: boolean;
  textFileModal: TextFileModalViewModel;
  confirmTextFileUpload: (data: {
    record_name: string;
    content: string;
    file_extension: "txt" | "md" | "srt" | "vtt";
    transcript_format?:
      | "speaker-text"
      | "timed-subtitles"
      | "tab-separated"
      | "plain-text"
      | "aseed"
      | "datetime-speaker";
    upload_source?: UploadSource;
  }) => Promise<void>;
  hideTextFileModal: () => void;
  getUploadTask: (taskId: string) => UploadTaskViewModel | undefined;
  subscribeToTask: (taskId: string, callback: () => void) => () => void;
}

const UploadFilesContext = createContext<UploadFilesContextType | null>(null);

export const UploadFilesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { invalidateRecords } = useRecordsMutations();

  const refreshRecords = useCallback(async () => {
    await Promise.resolve(invalidateRecords());
  }, [invalidateRecords]);

  const [store] = useState(() => createUploadStore({ refreshRecords }));

  const subscribe = useCallback(
    (listener: () => void) => store.subscribe(listener),
    [store]
  );

  const tasksCacheRef = useRef<Map<string, UploadTaskViewModel>>(new Map());
  const serverTasksSnapshotRef = useRef<Map<string, UploadTaskViewModel> | null>(
    null
  );

  const computeTasksSnapshot = useCallback(() => {
    const previous = tasksCacheRef.current;
    const next = new Map<string, UploadTaskViewModel>();
    let changed = false;

    store.uploadFileTasks.forEach((taskDto, id) => {
      const candidate = mapUploadTaskDtoToViewModel(taskDto);
      const previousTask = previous.get(id);

      if (
        previousTask &&
        isSamePlaceholderRecord(
          previousTask.placeholderRecord,
          candidate.placeholderRecord
        )
      ) {
        candidate.placeholderRecord = previousTask.placeholderRecord;
      }

      const isSame = previousTask
        ? isSameTaskViewModel(previousTask, candidate)
        : false;

      if (!isSame) {
        changed = true;
      }

      next.set(id, isSame && previousTask ? previousTask : candidate);
    });

    if (!changed) {
      if (previous.size !== next.size) {
        changed = true;
      } else {
        for (const key of previous.keys()) {
          if (!next.has(key)) {
            changed = true;
            break;
          }
        }
      }
    }

    if (!changed) {
      return previous;
    }

    tasksCacheRef.current = next;
    return next;
  }, [store]);

  const getClientTasksSnapshot = useCallback(() => {
    const snapshot = computeTasksSnapshot();
    serverTasksSnapshotRef.current = snapshot;
    return snapshot;
  }, [computeTasksSnapshot]);

  const getServerTasksSnapshot = useCallback(() => {
    if (!serverTasksSnapshotRef.current) {
      serverTasksSnapshotRef.current = computeTasksSnapshot();
    }
    return serverTasksSnapshotRef.current;
  }, [computeTasksSnapshot]);

  const uploadTasksMap = useSyncExternalStore(
    subscribe,
    getClientTasksSnapshot,
    getServerTasksSnapshot
  );

  const textModalCacheRef = useRef<TextFileModalViewModel>({
    isVisible: false,
    data: null,
    projectId: undefined,
  });
  const serverTextModalSnapshotRef = useRef<TextFileModalViewModel | null>(null);

  const computeTextModalSnapshot = useCallback(() => {
    const { isVisible, draft } = store.textFileModal;
    const candidate: TextFileModalViewModel = {
      isVisible,
      data: draft
        ? {
            taskId: draft.taskId,
            name: draft.name,
            content: draft.content,
            extension: draft.extension,
            uploadSource: isUploadSource(draft.uploadSource)
              ? draft.uploadSource
              : undefined,
          }
        : null,
      projectId: draft?.projectId,
    };

    const previous = textModalCacheRef.current;

    if (isSameTextFileModal(previous, candidate)) {
      return previous;
    }

    const snapshot: TextFileModalViewModel = {
      isVisible: candidate.isVisible,
      data:
        candidate.data &&
        previous.data &&
        isSameTextFileModalData(previous.data, candidate.data)
          ? previous.data
          : candidate.data,
      projectId: candidate.projectId,
    };

    textModalCacheRef.current = snapshot;
    return snapshot;
  }, [store]);

  const getClientTextModalSnapshot = useCallback(() => {
    const snapshot = computeTextModalSnapshot();
    serverTextModalSnapshotRef.current = snapshot;
    return snapshot;
  }, [computeTextModalSnapshot]);

  const getServerTextModalSnapshot = useCallback(() => {
    if (!serverTextModalSnapshotRef.current) {
      serverTextModalSnapshotRef.current = computeTextModalSnapshot();
    }
    return serverTextModalSnapshotRef.current;
  }, [computeTextModalSnapshot]);

  const textFileModal = useSyncExternalStore(
    subscribe,
    getClientTextModalSnapshot,
    getServerTextModalSnapshot
  );

  const serverIsUploadingSnapshotRef = useRef<boolean | null>(null);
  const getClientIsUploadingSnapshot = useCallback(() => {
    const value = store.isUploading;
    serverIsUploadingSnapshotRef.current = value;
    return value;
  }, [store]);

  const getServerIsUploadingSnapshot = useCallback(() => {
    if (serverIsUploadingSnapshotRef.current === null) {
      serverIsUploadingSnapshotRef.current = store.isUploading;
    }
    return serverIsUploadingSnapshotRef.current;
  }, [store]);

  const isUploading = useSyncExternalStore(
    subscribe,
    getClientIsUploadingSnapshot,
    getServerIsUploadingSnapshot
  );

  const contextValue = useMemo<UploadFilesContextType>(
    () => ({
      uploadNewFile: store.uploadFile,
      uploadMultipleFiles: store.uploadMultipleFiles,
      uploadFileTasks: uploadTasksMap,
      removeTask: store.removeTask,
      cancelUpload: store.cancelUpload,
      isUploading,
      textFileModal,
      confirmTextFileUpload: store.confirmTextFileUpload,
      hideTextFileModal: store.hideTextFileModal,
      getUploadTask: (taskId: string) => uploadTasksMap.get(taskId),
      subscribeToTask: store.subscribeToTask,
    }),
    [store, uploadTasksMap, isUploading, textFileModal]
  );

  return (
    <UploadFilesContext.Provider value={contextValue}>
      {children}
    </UploadFilesContext.Provider>
  );
};

export const useUploadFilesContext = (): UploadFilesContextType => {
  const context = useContext(UploadFilesContext);
  if (!context) {
    throw new Error(
      "useUploadFilesContext must be used within a UploadFilesProvider"
    );
  }
  return context;
};

export const useUploadTask = (
  taskId?: string
): UploadTaskViewModel | undefined => {
  const { getUploadTask, subscribeToTask } = useUploadFilesContext();

  const subscribe = useCallback(
    (listener: () => void) => {
      if (!taskId) {
        return () => {};
      }
      return subscribeToTask(taskId, listener);
    },
    [subscribeToTask, taskId]
  );

  return useSyncExternalStore(
    subscribe,
    () => (taskId ? getUploadTask(taskId) : undefined),
    () => (taskId ? getUploadTask(taskId) : undefined)
  );
};
